import { __decorate, __param } from 'vscode/external/tslib/tslib.es6.js';
import { Extensions, registerWorkbenchContribution2 } from 'vscode/vscode/vs/workbench/common/contributions';
import { Registry } from 'vscode/vscode/vs/platform/registry/common/platform';
import { ILabelService } from 'vscode/vscode/vs/platform/label/common/label.service';
import { OS, isWeb } from 'vscode/vscode/vs/base/common/platform';
import { Schemas } from 'vscode/vscode/vs/base/common/network';
import { IRemoteAgentService } from 'vscode/vscode/vs/workbench/services/remote/common/remoteAgentService.service';
import { ILoggerService } from 'vscode/vscode/vs/platform/log/common/log.service';
import { localizeWithPath, localize2WithPath } from 'vscode/vscode/vs/nls';
import { Disposable } from 'vscode/vscode/vs/base/common/lifecycle';
import { Extensions as Extensions$1 } from 'vscode/vscode/vs/platform/configuration/common/configurationRegistry';
import { IFileService } from 'vscode/vscode/vs/platform/files/common/files.service';
import { IDialogService, IFileDialogService } from 'vscode/vscode/vs/platform/dialogs/common/dialogs.service';
import { IWorkbenchEnvironmentService } from 'vscode/vscode/vs/workbench/services/environment/common/environmentService.service';
import { IWorkspaceContextService } from 'vscode/vscode/vs/platform/workspace/common/workspace.service';
import { firstOrDefault } from 'vscode/vscode/vs/base/common/arrays';
import { Action2, registerAction2 } from 'vscode/vscode/vs/platform/actions/common/actions';
import { Categories } from 'vscode/vscode/vs/platform/action/common/actionCommonCategories';
import { PersistentConnection } from 'vscode/vscode/vs/platform/remote/common/remoteAgentConnection';
import { IDownloadService } from 'vscode/vscode/vs/platform/download/common/download.service';
import { DownloadServiceChannel } from '../../../../platform/download/common/downloadIpc.js';
import { RemoteLoggerChannelClient } from '../../../../platform/log/common/logIpc.js';

const _moduleId = "vs/workbench/contrib/remote/common/remote.contribution";
let LabelContribution = class LabelContribution {
    static { this.ID = 'workbench.contrib.remoteLabel'; }
    constructor(labelService, remoteAgentService) {
        this.labelService = labelService;
        this.remoteAgentService = remoteAgentService;
        this.registerFormatters();
    }
    registerFormatters() {
        this.remoteAgentService.getEnvironment().then(remoteEnvironment => {
            const os = remoteEnvironment?.os || OS;
            const formatting = {
                label: '${path}',
                separator: os === 1  ? '\\' : '/',
                tildify: os !== 1 ,
                normalizeDriveLetter: os === 1 ,
                workspaceSuffix: isWeb ? undefined : Schemas.vscodeRemote
            };
            this.labelService.registerFormatter({
                scheme: Schemas.vscodeRemote,
                formatting
            });
            if (remoteEnvironment) {
                this.labelService.registerFormatter({
                    scheme: Schemas.vscodeUserData,
                    formatting
                });
            }
        });
    }
};
LabelContribution = ( (__decorate([
    ( (__param(0, ILabelService))),
    ( (__param(1, IRemoteAgentService)))
], LabelContribution)));
let RemoteChannelsContribution = class RemoteChannelsContribution extends Disposable {
    constructor(remoteAgentService, downloadService, loggerService) {
        super();
        const connection = remoteAgentService.getConnection();
        if (connection) {
            connection.registerChannel('download', ( (new DownloadServiceChannel(downloadService))));
            connection.withChannel('logger', async (channel) => this._register(( (new RemoteLoggerChannelClient(loggerService, channel)))));
        }
    }
};
RemoteChannelsContribution = ( (__decorate([
    ( (__param(0, IRemoteAgentService))),
    ( (__param(1, IDownloadService))),
    ( (__param(2, ILoggerService)))
], RemoteChannelsContribution)));
let RemoteInvalidWorkspaceDetector = class RemoteInvalidWorkspaceDetector extends Disposable {
    static { this.ID = 'workbench.contrib.remoteInvalidWorkspaceDetector'; }
    constructor(fileService, dialogService, environmentService, contextService, fileDialogService, remoteAgentService) {
        super();
        this.fileService = fileService;
        this.dialogService = dialogService;
        this.environmentService = environmentService;
        this.contextService = contextService;
        this.fileDialogService = fileDialogService;
        if (this.environmentService.remoteAuthority) {
            remoteAgentService.getEnvironment().then(remoteEnv => {
                if (remoteEnv) {
                    this.validateRemoteWorkspace();
                }
            });
        }
    }
    async validateRemoteWorkspace() {
        const workspace = this.contextService.getWorkspace();
        const workspaceUriToStat = workspace.configuration ?? firstOrDefault(workspace.folders)?.uri;
        if (!workspaceUriToStat) {
            return;
        }
        const exists = await this.fileService.exists(workspaceUriToStat);
        if (exists) {
            return;
        }
        const res = await this.dialogService.confirm({
            type: 'warning',
            message: ( localizeWithPath(_moduleId, 0, "Workspace does not exist")),
            detail: ( localizeWithPath(_moduleId, 1, "Please select another workspace to open.")),
            primaryButton: ( localizeWithPath(_moduleId, 2, "&&Open Workspace..."))
        });
        if (res.confirmed) {
            if (workspace.configuration) {
                return this.fileDialogService.pickWorkspaceAndOpen({});
            }
            return this.fileDialogService.pickFolderAndOpen({});
        }
    }
};
RemoteInvalidWorkspaceDetector = ( (__decorate([
    ( (__param(0, IFileService))),
    ( (__param(1, IDialogService))),
    ( (__param(2, IWorkbenchEnvironmentService))),
    ( (__param(3, IWorkspaceContextService))),
    ( (__param(4, IFileDialogService))),
    ( (__param(5, IRemoteAgentService)))
], RemoteInvalidWorkspaceDetector)));
const workbenchContributionsRegistry = ( (Registry.as(Extensions.Workbench)));
registerWorkbenchContribution2(LabelContribution.ID, LabelContribution, 1 );
workbenchContributionsRegistry.registerWorkbenchContribution(RemoteChannelsContribution, 3 );
registerWorkbenchContribution2(RemoteInvalidWorkspaceDetector.ID, RemoteInvalidWorkspaceDetector, 1 );
{
    class TriggerReconnectAction extends Action2 {
        constructor() {
            super({
                id: 'workbench.action.triggerReconnect',
                title: ( localize2WithPath(_moduleId, 3, 'Connection: Trigger Reconnect')),
                category: Categories.Developer,
                f1: true,
            });
        }
        async run(accessor) {
            PersistentConnection.debugTriggerReconnection();
        }
    }
    class PauseSocketWriting extends Action2 {
        constructor() {
            super({
                id: 'workbench.action.pauseSocketWriting',
                title: ( localize2WithPath(_moduleId, 4, 'Connection: Pause socket writing')),
                category: Categories.Developer,
                f1: true,
            });
        }
        async run(accessor) {
            PersistentConnection.debugPauseSocketWriting();
        }
    }
    registerAction2(TriggerReconnectAction);
    registerAction2(PauseSocketWriting);
}
const extensionKindSchema = {
    type: 'string',
    enum: [
        'ui',
        'workspace'
    ],
    enumDescriptions: [
        ( localizeWithPath(
            _moduleId,
            5,
            "UI extension kind. In a remote window, such extensions are enabled only when available on the local machine."
        )),
        ( localizeWithPath(
            _moduleId,
            6,
            "Workspace extension kind. In a remote window, such extensions are enabled only when available on the remote."
        ))
    ],
};
( (Registry.as(Extensions$1.Configuration)))
    .registerConfiguration({
    id: 'remote',
    title: ( localizeWithPath(_moduleId, 7, "Remote")),
    type: 'object',
    properties: {
        'remote.extensionKind': {
            type: 'object',
            markdownDescription: ( localizeWithPath(
                _moduleId,
                8,
                "Override the kind of an extension. `ui` extensions are installed and run on the local machine while `workspace` extensions are run on the remote. By overriding an extension's default kind using this setting, you specify if that extension should be installed and enabled locally or remotely."
            )),
            patternProperties: {
                '([a-z0-9A-Z][a-z0-9-A-Z]*)\\.([a-z0-9A-Z][a-z0-9-A-Z]*)$': {
                    oneOf: [{ type: 'array', items: extensionKindSchema }, extensionKindSchema],
                    default: ['ui'],
                },
            },
            default: {
                'pub.name': ['ui']
            }
        },
        'remote.restoreForwardedPorts': {
            type: 'boolean',
            markdownDescription: ( localizeWithPath(_moduleId, 9, "Restores the ports you forwarded in a workspace.")),
            default: true
        },
        'remote.autoForwardPorts': {
            type: 'boolean',
            markdownDescription: ( localizeWithPath(
                _moduleId,
                10,
                "When enabled, new running processes are detected and ports that they listen on are automatically forwarded. Disabling this setting will not prevent all ports from being forwarded. Even when disabled, extensions will still be able to cause ports to be forwarded, and opening some URLs will still cause ports to forwarded."
            )),
            default: true
        },
        'remote.autoForwardPortsSource': {
            type: 'string',
            markdownDescription: ( localizeWithPath(
                _moduleId,
                11,
                "Sets the source from which ports are automatically forwarded when {0} is true. On Windows and macOS remotes, the `process` and `hybrid` options have no effect and `output` will be used.",
                '`#remote.autoForwardPorts#`'
            )),
            enum: ['process', 'output', 'hybrid'],
            enumDescriptions: [
                ( localizeWithPath(
                    _moduleId,
                    12,
                    "Ports will be automatically forwarded when discovered by watching for processes that are started and include a port."
                )),
                ( localizeWithPath(
                    _moduleId,
                    13,
                    "Ports will be automatically forwarded when discovered by reading terminal and debug output. Not all processes that use ports will print to the integrated terminal or debug console, so some ports will be missed. Ports forwarded based on output will not be \"un-forwarded\" until reload or until the port is closed by the user in the Ports view."
                )),
                ( localizeWithPath(
                    _moduleId,
                    14,
                    "Ports will be automatically forwarded when discovered by reading terminal and debug output. Not all processes that use ports will print to the integrated terminal or debug console, so some ports will be missed. Ports will be \"un-forwarded\" by watching for processes that listen on that port to be terminated."
                ))
            ],
            default: 'process'
        },
        'remote.autoForwardPortsFallback': {
            type: 'number',
            default: 20,
            markdownDescription: ( localizeWithPath(
                _moduleId,
                15,
                "The number of auto forwarded ports that will trigger the switch from `process` to `hybrid` when automatically forwarding ports and `remote.autoForwardPortsSource` is set to `process` by default. Set to `0` to disable the fallback. When `remote.autoForwardPortsFallback` hasn't been configured, but `remote.autoForwardPortsSource` has, `remote.autoForwardPortsFallback` will be treated as though it's set to `0`."
            ))
        },
        'remote.forwardOnOpen': {
            type: 'boolean',
            description: ( localizeWithPath(
                _moduleId,
                16,
                "Controls whether local URLs with a port will be forwarded when opened from the terminal and the debug console."
            )),
            default: true
        },
        'remote.portsAttributes': {
            type: 'object',
            patternProperties: {
                '(^\\d+(-\\d+)?$)|(.+)': {
                    type: 'object',
                    description: ( localizeWithPath(
                        _moduleId,
                        17,
                        "A port, range of ports (ex. \"40000-55000\"), host and port (ex. \"db:1234\"), or regular expression (ex. \".+\\\\/server.js\").  For a port number or range, the attributes will apply to that port number or range of port numbers. Attributes which use a regular expression will apply to ports whose associated process command line matches the expression."
                    )),
                    properties: {
                        'onAutoForward': {
                            type: 'string',
                            enum: ['notify', 'openBrowser', 'openBrowserOnce', 'openPreview', 'silent', 'ignore'],
                            enumDescriptions: [
                                ( localizeWithPath(
                                    _moduleId,
                                    18,
                                    "Shows a notification when a port is automatically forwarded."
                                )),
                                ( localizeWithPath(
                                    _moduleId,
                                    19,
                                    "Opens the browser when the port is automatically forwarded. Depending on your settings, this could open an embedded browser."
                                )),
                                ( localizeWithPath(
                                    _moduleId,
                                    20,
                                    "Opens the browser when the port is automatically forwarded, but only the first time the port is forward during a session. Depending on your settings, this could open an embedded browser."
                                )),
                                ( localizeWithPath(
                                    _moduleId,
                                    21,
                                    "Opens a preview in the same window when the port is automatically forwarded."
                                )),
                                ( localizeWithPath(
                                    _moduleId,
                                    22,
                                    "Shows no notification and takes no action when this port is automatically forwarded."
                                )),
                                ( localizeWithPath(_moduleId, 23, "This port will not be automatically forwarded."))
                            ],
                            description: ( localizeWithPath(
                                _moduleId,
                                24,
                                "Defines the action that occurs when the port is discovered for automatic forwarding"
                            )),
                            default: 'notify'
                        },
                        'elevateIfNeeded': {
                            type: 'boolean',
                            description: ( localizeWithPath(
                                _moduleId,
                                25,
                                "Automatically prompt for elevation (if needed) when this port is forwarded. Elevate is required if the local port is a privileged port."
                            )),
                            default: false
                        },
                        'label': {
                            type: 'string',
                            description: ( localizeWithPath(_moduleId, 26, "Label that will be shown in the UI for this port.")),
                            default: ( localizeWithPath(_moduleId, 27, "Application"))
                        },
                        'requireLocalPort': {
                            type: 'boolean',
                            markdownDescription: ( localizeWithPath(
                                _moduleId,
                                28,
                                "When true, a modal dialog will show if the chosen local port isn't used for forwarding."
                            )),
                            default: false
                        },
                        'protocol': {
                            type: 'string',
                            enum: ['http', 'https'],
                            description: ( localizeWithPath(_moduleId, 29, "The protocol to use when forwarding this port."))
                        }
                    },
                    default: {
                        'label': ( localizeWithPath(_moduleId, 27, "Application")),
                        'onAutoForward': 'notify'
                    }
                }
            },
            markdownDescription: ( localizeWithPath(
                _moduleId,
                30,
                "Set properties that are applied when a specific port number is forwarded. For example:\n\n```\n\"3000\": {\n  \"label\": \"Application\"\n},\n\"40000-55000\": {\n  \"onAutoForward\": \"ignore\"\n},\n\".+\\\\/server.js\": {\n \"onAutoForward\": \"openPreview\"\n}\n```"
            )),
            defaultSnippets: [{ body: { '${1:3000}': { label: '${2:Application}', onAutoForward: 'openPreview' } } }],
            errorMessage: ( localizeWithPath(
                _moduleId,
                31,
                "Must be a port number, range of port numbers, or regular expression."
            )),
            additionalProperties: false,
            default: {
                '443': {
                    'protocol': 'https'
                },
                '8443': {
                    'protocol': 'https'
                }
            }
        },
        'remote.otherPortsAttributes': {
            type: 'object',
            properties: {
                'onAutoForward': {
                    type: 'string',
                    enum: ['notify', 'openBrowser', 'openPreview', 'silent', 'ignore'],
                    enumDescriptions: [
                        ( localizeWithPath(
                            _moduleId,
                            18,
                            "Shows a notification when a port is automatically forwarded."
                        )),
                        ( localizeWithPath(
                            _moduleId,
                            19,
                            "Opens the browser when the port is automatically forwarded. Depending on your settings, this could open an embedded browser."
                        )),
                        ( localizeWithPath(
                            _moduleId,
                            21,
                            "Opens a preview in the same window when the port is automatically forwarded."
                        )),
                        ( localizeWithPath(
                            _moduleId,
                            22,
                            "Shows no notification and takes no action when this port is automatically forwarded."
                        )),
                        ( localizeWithPath(_moduleId, 23, "This port will not be automatically forwarded."))
                    ],
                    description: ( localizeWithPath(
                        _moduleId,
                        24,
                        "Defines the action that occurs when the port is discovered for automatic forwarding"
                    )),
                    default: 'notify'
                },
                'elevateIfNeeded': {
                    type: 'boolean',
                    description: ( localizeWithPath(
                        _moduleId,
                        25,
                        "Automatically prompt for elevation (if needed) when this port is forwarded. Elevate is required if the local port is a privileged port."
                    )),
                    default: false
                },
                'label': {
                    type: 'string',
                    description: ( localizeWithPath(_moduleId, 26, "Label that will be shown in the UI for this port.")),
                    default: ( localizeWithPath(_moduleId, 27, "Application"))
                },
                'requireLocalPort': {
                    type: 'boolean',
                    markdownDescription: ( localizeWithPath(
                        _moduleId,
                        28,
                        "When true, a modal dialog will show if the chosen local port isn't used for forwarding."
                    )),
                    default: false
                },
                'protocol': {
                    type: 'string',
                    enum: ['http', 'https'],
                    description: ( localizeWithPath(_moduleId, 29, "The protocol to use when forwarding this port."))
                }
            },
            defaultSnippets: [{ body: { onAutoForward: 'ignore' } }],
            markdownDescription: ( localizeWithPath(
                _moduleId,
                32,
                "Set default properties that are applied to all ports that don't get properties from the setting {0}. For example:\n\n```\n{\n  \"onAutoForward\": \"ignore\"\n}\n```",
                '`#remote.portsAttributes#`'
            )),
            additionalProperties: false
        },
        'remote.localPortHost': {
            type: 'string',
            enum: ['localhost', 'allInterfaces'],
            default: 'localhost',
            description: ( localizeWithPath(
                _moduleId,
                33,
                "Specifies the local host name that will be used for port forwarding."
            ))
        }
    }
});

export { LabelContribution };
